/**
 * Meta click identifiers, for server-side conversion reporting.
 *
 * Meta scores how well it can recognise the person behind each conversion we
 * report ("event match quality"). We sat at 6.1/10 because we sent an email
 * hash and nothing else — in particular we threw away Meta's own click id,
 * which is the strongest signal it has for tying a sale back to an ad.
 *
 * Two identifiers matter, and neither is a personal detail of the member:
 *   fbc — Meta's id for the ad click that brought them here (from ?fbclid=)
 *   fbp — the browser id our own Meta pixel writes on public pages
 * Both are Meta's own values being handed back to Meta. They are read here at
 * the landing page and kept until registration, which may be several pages later.
 *
 * Sent alongside the scrambled email; see backend/services/meta_capi.py.
 */

const STORAGE_KEY = 'meta_attribution';

/** Read a browser cookie by name, or null. */
function readCookie(name) {
  try {
    const match = document.cookie.match(new RegExp(`(^|;\s*)${name}=([^;]*)`));
    return match ? decodeURIComponent(match[2]) : null;
  } catch (e) {
    return null;
  }
}

/**
 * Capture the click ids on arrival. Call once, as early as possible — the
 * ?fbclid= parameter is present only on the landing URL and is gone by the
 * time the user reaches the registration form.
 */
export function captureMetaClickIds() {
  try {
    const fbclid = new URLSearchParams(window.location.search).get('fbclid');
    if (!fbclid) return;

    const existing = read();
    // Keep the first click of the visit: re-stamping on a later ad click would
    // credit the wrong campaign for a journey that was already under way.
    if (existing.fbclid === fbclid) return;

    // Meta's format. The pixel writes this cookie itself on public pages; we
    // build the same value so the click still survives when the pixel is
    // blocked or the user lands on a page that does not load it.
    const fbc = `fb.1.${Date.now()}.${fbclid}`;
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ fbclid, fbc }));
  } catch (e) {
    /* private mode, or storage disabled — attribution is best-effort */
  }
}

function read() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}') || {};
  } catch (e) {
    return {};
  }
}

/**
 * The identifiers to send with a registration.
 *
 * Prefers the pixel's own `_fbc` cookie over our stored copy: when the pixel
 * did run, its value carries the real click timestamp rather than the moment
 * we happened to read the URL.
 */
export function getMetaAttribution() {
  const stored = read();
  const out = {};
  const fbc = readCookie('_fbc') || stored.fbc;
  const fbp = readCookie('_fbp');
  if (stored.fbclid) out.fbclid = stored.fbclid;
  if (fbc) out.fbc = fbc;
  if (fbp) out.fbp = fbp;
  return out;
}

/**
 * One id shared by the browser event and the server event for the same signup,
 * so Meta collapses the pair instead of counting two registrations.
 */
export function newMetaEventId() {
  try {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  } catch (e) { /* fall through */ }
  return `reg_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

/** Forget the captured click once it has been reported. */
export function clearMetaAttribution() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) { /* nothing to clear */ }
}
